import React, { useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useUIDSeed } from 'react-uid';

import classNames from 'classnames';

import styles from './TextArea.module.scss';

export type TextAreaProps = {
  ariaLabel?: string;
  className?: string;
  maxLength?: number;
  id?: string;
  name?: string;
  placeholder?: string;
  resizeable?: boolean;
  showCharacterCount?: boolean;
  value?: string;
  minRows?: number;
  onChange?(e: React.ChangeEvent, newValue?: string, name?: string): void;
  onFocus?(e: React.FocusEvent): void;
  onBlur?(e: React.FocusEvent): void;
};

export const TextArea: React.FunctionComponent<TextAreaProps> = ({
  ariaLabel,
  maxLength,
  resizeable = false,
  showCharacterCount = false,
  value = '',
  ...props
}) => {
  const [focus, setFocus] = useState(false);
  const seed = useUIDSeed();
  const newValue = maxLength ? value.slice(0, maxLength) : value;
  const length = newValue.length;

  const onChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value: newValue } = ev.currentTarget;
    if (props.onChange) props.onChange(ev, newValue, name);
  };

  const onFocus = (ev: React.FocusEvent) => {
    if (props.onFocus) props.onFocus(ev);
    setFocus(true);
  };

  const onBlur = (ev: React.FocusEvent) => {
    if (props.onBlur) props.onBlur(ev);
    setFocus(false);
  };

  const className = classNames(props.className, styles.textarea, {
    [styles.resizeable]: resizeable,
  });

  const uid = props.id || seed('textarea');

  const describedBy =
    showCharacterCount && maxLength ? `${uid}.count` : undefined;

  return (
    <div className={styles.wrap}>
      <TextareaAutosize
        {...props}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        className={className}
        id={uid}
        maxLength={maxLength}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        value={newValue}
        minRows={props.minRows}
      />

      {showCharacterCount && maxLength && (
        <div
          id={`${uid}.count`}
          aria-atomic="true"
          aria-label={`${maxLength - length} characters remaining`}
          aria-live={focus ? 'polite' : 'off'}
          className={styles.count}
        >
          {maxLength - length}
        </div>
      )}
    </div>
  );
};
